import { makeStyles } from 'tss-react/mui';
import { FC, Fragment, useRef } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useIntl } from 'react-intl';
import AddIcon from '@mui/icons-material/Add';

const useStyles = makeStyles()(() => ({
  header: {
    margin: '20px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    borderLeft: '5px solid #E1001F',
    paddingLeft: 10,
    fontWeight: 600,
    fontSize: 20,
    lineHeight: '28px',
    color: '#101828',
  },
  card: {
    boxSizing: 'border-box',
    background: '#FFF',
    border: '1px solid #E5E7EB',
    borderRadius: 10,
    padding: 25,
    boxShadow: '0 1px 3px rgba(0,0,0,.1), 0 1px 2px rgba(0,0,0,.1)',
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: 18,
    lineHeight: '28px',
    color: '#364153',
    marginBottom: 12,
  },
  dividerGroup: {
    '& > *': {
      padding: 'var(--divider-padding, 8px) 0',
    },
    '& > *:first-of-type': {
      paddingTop: 0,
    },
    '& > *:last-of-type': {
      paddingBottom: 0,
    },
    '& > * + *': {
      borderTop: '1px solid var(--divider-color, #E5E7EB)',
    },
  },
  spaceBetween: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionButtons: {
    display: 'flex',
    gap: 10,
    marginLeft: 5,
    flexShrink: 0,
  },
  rowContent: {
    fontSize: '1rem',
    flex: 1,
    minWidth: 0,
  },
}));

const UploadIcon = () => {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M4 19H20V12H22V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V12H4V19ZM13 9V16H11V9H6L12 3L18 9H13Z"
        fill="currentColor"
      />
    </svg>
  );
};

type Props = {
  title: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
};

export const SectionCard = ({ title, children, actions }: Props) => {
  const { classes } = useStyles();
  return (
    <div>
      <div className={classes.header}>
        <div className={classes.title}>{title}</div>

        {actions && <div>{actions}</div>}
      </div>

      <div className={classes.card}>{children}</div>
    </div>
  );
};

type SectionProps = {
  title?: React.ReactNode;
  children: React.ReactNode;
};

export const Section: FC<SectionProps> = ({ title, children }) => {
  const { classes } = useStyles();

  return (
    <div>
      {title && <div className={classes.sectionTitle}>{title}</div>}

      {children}
    </div>
  );
};

type DividerGroupProps = {
  children: React.ReactNode;
  paddingY?: number;
  dividerColor?: string;
};

export const DividerGroup: FC<DividerGroupProps> = ({ children, paddingY, dividerColor }) => {
  const { classes } = useStyles();

  return (
    <div
      className={classes.dividerGroup}
      style={
        {
          '--divider-padding': paddingY ? `${paddingY}px` : undefined,
          '--divider-color': dividerColor,
        } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
};

export enum RowActionEnum {
  VIEW = 'VIEW',
  APPROVE = 'APPROVE',
  REJECT = 'REJECT',
  UPLOAD_TO_CUSSEARCH = 'UPLOAD_TO_CUSSEARCH',
  UPLOAD_PDF = 'UPLOAD_PDF',
  ADD = 'ADD',
}

type ActionConfig = {
  text: string;
};
const ACTION_CONFIG: Record<RowActionEnum, ActionConfig> = {
  [RowActionEnum.VIEW]: {
    text: 'recruit.button.view',
  },
  [RowActionEnum.APPROVE]: {
    text: 'recruit.button.approve',
  },
  [RowActionEnum.REJECT]: {
    text: 'recruit.button.reject',
  },
  [RowActionEnum.UPLOAD_TO_CUSSEARCH]: {
    text: 'recruit.personalParticular.uploadToCusSearch',
  },
  [RowActionEnum.UPLOAD_PDF]: {
    text: 'recruit.button.upload',
  },
  [RowActionEnum.ADD]: {
    text: 'recruit.button.add',
  },
};

export type RowAction = {
  type: RowActionEnum;
  onClick?: (e?: React.ChangeEvent<HTMLInputElement>) => void;
  visible?: boolean;
  disabled?: boolean;
};

type RowProps = {
  children: React.ReactNode;
  right?: React.ReactNode;
  minHeight?: number | string;
  actions?: {
    type: RowActionEnum;
    onClick?: (e?: React.ChangeEvent<HTMLInputElement>) => void;
    loading?: boolean;
    disabled?: boolean;
    visible?: boolean;
  }[];
  actionAlign?: 'center' | 'top';
  actionMarginTop?: number;
};

const UploadButton = ({ action, text }: { action: any; text: string }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={fileInputRef} hidden type="file" accept=".pdf" onChange={action.onClick} />
      <Button
        disabled={action.disabled || action.loading}
        onClick={() => fileInputRef.current?.click()}
        startIcon={<UploadIcon />}
        variant="contained"
        color="secondary"
        sx={{
          width: 110,
          '& svg': {
            width: 16,
            height: 16,
          },
        }}
      >
        {text}
        {action.loading && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
      </Button>
    </>
  );
};

export const Row = ({ children, right, minHeight, actions, actionAlign = 'center', actionMarginTop = 0 }: RowProps) => {
  const { classes } = useStyles();
  const intl = useIntl();

  const Translation = (id: string) => intl.formatMessage({ id });

  return (
    <div
      className={classes.spaceBetween}
      style={{
        minHeight,
        alignItems: actionAlign === 'center' ? 'center' : 'flex-start',
      }}
    >
      <div className={classes.rowContent}>{children}</div>

      {right && <div>{right}</div>}

      {actions && (
        <div
          className={classes.actionButtons}
          style={{
            marginTop: actionMarginTop,
          }}
        >
          {actions
            ?.filter((action) => action.visible !== false)
            .map((action, index) => {
              const config = ACTION_CONFIG[action.type];
              if (action.type === 'UPLOAD_PDF') {
                return <UploadButton key={`${action.type}-${index}`} action={action} text={Translation(config.text)} />;
              } else if (action.type === 'ADD') {
                return (
                  <Button
                    key={action.type}
                    startIcon={<AddIcon />}
                    variant="contained"
                    color="secondary"
                    sx={{
                      width: 110,
                      '& svg': {
                        width: 16,
                        height: 16,
                      },
                    }}
                    disabled={action.disabled || action.loading}
                    onClick={() => action.onClick?.()}
                  >
                    {Translation(config.text)}
                    {action.loading && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
                  </Button>
                );
              } else {
                return (
                  <Button
                    key={action.type}
                    variant="contained"
                    color="secondary"
                    disabled={action.disabled || action.loading}
                    onClick={() => action.onClick?.()}
                  >
                    {Translation(config.text)}
                    {action.loading && <CircularProgress style={{ marginLeft: 8 }} size={15} />}
                  </Button>
                );
              }
            })}
        </div>
      )}
    </div>
  );
};
